import { Box } from '@mui/material';
import { useNavigation } from '@src/App/Components/Core/Navigation/NavigationContext';
import React from 'react';

import { MvpTabContainer } from '../LayoutElements/MvpTabContainer/MvpTabContainer';
import './InnerLayout.css';
import { HoverFab } from '../../HoverFab/HoverFab';
import ContentHeader from '../LayoutElements/ContentHeader/ContentHeader';
import type { ITabConfig } from '../LayoutElements/MvpTabContainer';

interface InnerLayoutProps {
    inDialog?: boolean;
    title?: React.ReactNode;
    subTitle?: React.ReactNode;
    caption?: React.ReactNode;
    icon?: React.ReactNode;
    bigLeftBox?: JSX.Element;
    informations?: React.ReactNode;
    buttons?: React.ReactNode;
    dashboardSwitcher?: React.ReactNode;
    tabs?: ITabConfig[];
    showTabIcons?: boolean;
    hideHeader?: boolean;
    children: React.ReactNode;
}

/**
 * InnerLayout component
 *
 * @param {InnerLayoutProps} props - Props containing the data for the InnerLayout component
 * @returns {JSX.Element} The JSX element representing the InnerLayout component
 */
function InnerLayout(props: InnerLayoutProps) {
    const { subNav } = useNavigation();
    const tabs = subNav?.tabs ?? null;
    const hasSubNav = tabs && tabs.length > 0;

    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 30px)', md: '1200px' },
                mx: 'auto',
                px: { xs: 1, sm: 3, md: 4 },
                py: { xs: 2, md: 3 },
            }}
        >
            {!props.hideHeader && (
                <ContentHeader
                    inDialog={props.inDialog}
                    title={props.title}
                    subTitle={props.subTitle}
                    caption={props.caption}
                    icon={props.icon}
                    bigLeftBox={props.bigLeftBox}
                    buttons={props.buttons}
                    dashboardSwitcher={props.dashboardSwitcher}
                >
                    {props.informations}
                </ContentHeader>
            )}

            <Box
                sx={{
                    mt: props.inDialog ? 0 : { xs: 1, sm: 2, md: 3 },
                    mb: { xs: 8, md: 4 }, // Space for HoverFab
                }}
            >
                {hasSubNav && (
                    <Box
                        sx={{
                            mb: 2,
                            mt: { xs: -2, md: -3 }, // Negative margin to attach to header
                            mx: { xs: -1, sm: -3, md: -4 }, // Negative horizontal margin to extend full width
                        }}
                    >
                        <MvpTabContainer tabs={tabs} showIcons={props.showTabIcons} />
                    </Box>
                )}
                {props.children}
            </Box>

            {!props.inDialog && <HoverFab />}
        </Box>
    );
}

export default InnerLayout;
